import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useCountryDetailByIdQuery,
  useCountryDetailsUpdateMutation,
  useCountryQuery,
  useCountryTagsQuery,
  useRegionsListQuery,
} from 'api';
import { FormInstance, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { ICountryDetailsUpdate } from './index';
import { Region } from 'models/Region';

export const useCountryDetailsEdit = (props: { form: FormInstance } & ICountryDetailsUpdate) => {
  const { onCancel, form, open } = props;
  const { id } = useParams() as { id: string };
  const { data: countryDetails = {} } = useCountryDetailByIdQuery({ id });
  const { data: tagData = [] } = useCountryTagsQuery();
  const { data: countryData = [] } = useCountryQuery();
  const { data: regions = [] } = useRegionsListQuery();
  const [subRegions, setSubRegions] = useState<{ value: string; label: string }[]>([]);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);

  const [updateCountryDetails, { isLoading: detailsUpdateIsLoading }] = useCountryDetailsUpdateMutation();
  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onCancelModal = useCallback(() => {
    form.resetFields();
    setIsSaveBtnDisabled(true);
    onCancel();
  }, [form, onCancel]);

  const regionsMapping = useMemo(() => {
    const mapping: Record<string, Region> = {};
    regions.forEach((i: Region) => {
      mapping[i.id] = i;
    });
    return mapping;
  }, [regions]);

  const initialValues = useMemo(() => {
    return {
      countryId: countryDetails?.id,
      languageIds: countryDetails.languages?.map((i: { id: string }) => i.id),
      subRegions: countryDetails.subRegions,
      additionalNotes: countryDetails.additionalNotes,
      regionIds: countryDetails.regions?.map((i: { id: string }) => i.id),
      tags: countryDetails.manualTags,
    };
  }, [countryDetails]);

  const setSubRegionsToForm = useCallback(
    (regionIds: string[]) => {
      const subRegSet = new Set<string>();

      regionIds.forEach((id: string) => {
        (regionsMapping[id]?.subRegion || []).forEach((sub) => subRegSet.add(sub));
      });

      const subRegs = Array.from(subRegSet);
      const subRegValues = (form.getFieldValue('subRegions') || []).filter((i: string) => subRegSet.has(i));

      form.setFieldValue('subRegions', subRegValues);
      setSubRegions(subRegs.map((i) => ({ label: i, value: i })));
    },
    [regionsMapping, form]
  );

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
      if (initialValues.regionIds?.length) {
        setSubRegionsToForm(initialValues.regionIds);
      }
    }
  }, [open, form, initialValues, setSubRegionsToForm]);

  const checkErrors = useCallback(
    async (values: Record<string, any>) => {
      let isDisabled = false;
      try {
        await form.validateFields();
        isDisabled = false;
      } catch (errorInfo: any) {
        isDisabled = !!errorInfo?.errorFields?.length;
      } finally {
        setIsSaveBtnDisabled(isDisabled);
      }

      if (values.regionIds) {
        setSubRegionsToForm(values.regionIds);
      }
    },
    [form, setSubRegionsToForm]
  );

  const handleError = useCallback(
    (error: any) => {
      if (!error.data.success) {
        const { data = {} } = error.data;
        Object.keys(data).forEach((_key: string) => {
          openNotificationWithIcon({
            type: 'error',
            message: data[_key],
          });
        });
      } else if (error.data.error) {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onFinish = useCallback(async () => {
    try {
      const payload = form.getFieldsValue();
      payload.countryId = id;
      const { data, error }: any = await updateCountryDetails(payload);
      if (data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'The country details were successfully updated.',
        });
        onCancelModal();
      } else {
        handleError(error);
      }
    } catch (err) {
      console.log(err, 'error');
    }
  }, [updateCountryDetails, openNotificationWithIcon, onCancelModal, id, form, handleError]);

  return {
    onFinish,
    countryData,
    contextHolder,
    tagData,
    checkErrors,
    isSaveBtnDisabled,
    onCancelModal,
    regions,
    subRegions,
    isLoading: detailsUpdateIsLoading,
  };
};
